import { useState } from 'react';
import { Button, Card, Modal } from '@playerbase/ui';
import type { FeedbackForm } from '@playerbase/types';

import { enableFeedbackForm } from '../../../games/feedback-forms-data.js';

interface EnableConfirmModalProps {
  open: boolean;
  form: FeedbackForm;
  onClose: () => void;
  onEnabled: (form: FeedbackForm) => void;
}

const ERROR_COPY: Partial<Record<string, string>> = {
  permission_check_required:
    'PlayerBase needs to re-check channel permissions before enabling. Use Re-check above.',
  survey_channel_required: 'Pick a survey channel before enabling.',
  response_channel_required: 'Pick a response channel before enabling.',
  submit_response_title_required:
    'Add a thank-you message title before enabling.',
  fields_required: 'Add at least one field before enabling.',
  form_already_enabled: 'This form is already enabled.',
  not_a_member: "You don't have permission to enable this form.",
};

// Confirm + perform Enable. Surfaces RPC errors inline; on
// permission_check_required the channel-access panel above the toolbar
// will already be live-updating from the most recent perm-check Realtime
// tick, so closing the modal sends the user straight to the recovery UX.

export function EnableConfirmModal({
  open,
  form,
  onClose,
  onEnabled,
}: EnableConfirmModalProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await enableFeedbackForm(form.id);
    if (!result.ok) {
      setBusy(false);
      setError(ERROR_COPY[result.error ?? 'unknown'] ?? 'Could not enable the form. Please try again.');
      return;
    }
    setBusy(false);
    setError(null);
    onEnabled(result.data!);
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) {
          setError(null);
          onClose();
        }
      }}
      labelledById={`enable-form-${form.id}`}
      busy={busy}
      size="sm"
    >
      <Card>
        <Card.Header>
          <Card.Title id={`enable-form-${form.id}`}>Enable form</Card.Title>
          <Card.Description>
            Posting <strong>{form.name}</strong> to Discord. Players can submit responses immediately.
            Fields can&rsquo;t be edited while the form is enabled.
          </Card.Description>
        </Card.Header>
        {error !== null ? (
          <Card.Content>
            <p className="text-sm text-danger-text">{error}</p>
          </Card.Content>
        ) : null}
        <Card.Footer>
          <Button
            variant="ghost"
            onClick={() => {
              setError(null);
              onClose();
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleConfirm()}
            disabled={busy}
          >
            {busy ? 'Enabling...' : 'Enable form'}
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}
